import React, { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Collapse,
  Box,
  Typography,
  Divider,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Stack,
  Badge,
  alpha,
  Grid,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert"; // Menu icon
import EditIcon from "@mui/icons-material/Edit"; // Edit option
import DeleteIcon from "@mui/icons-material/Delete"; // Remove option
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import TimelineIcon from "@mui/icons-material/Timeline";

const CaSettingsTable = ({ caSettings, handleEdit, handleRemove }) => {
  const [expandedRows, setExpandedRows] = useState({});
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedCa, setSelectedCa] = useState(null);

  const toggleExpand = (key) => {
    setExpandedRows((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleMenuOpen = (event, ca) => {
    setAnchorEl(event.currentTarget);
    setSelectedCa(ca);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedCa(null);
  };

  const handleDelete = () => {
    handleRemove(selectedCa);
    handleMenuClose();
  };

  const StatusChip = ({ enabled, label }) => (
    <Chip
      icon={enabled ? <CheckCircleIcon /> : <CancelIcon />}
      label={enabled ? "Enabled" : "Disabled"}
      size='small'
      color={enabled ? "success" : "default"}
      sx={{
        fontWeight: 500,
        "& .MuiChip-icon": {
          fontSize: "1rem",
        },
      }}
    />
  );

  return (
    <TableContainer
      component={Paper}
      sx={{
        borderRadius: 3,
        maxHeight: "68vh",
        overflowY: "auto",
        backgroundColor: "background.paper",
        "&::-webkit-scrollbar": {
          width: "8px",
          height: "8px",
        },
        "&::-webkit-scrollbar-track": {
          background: alpha("#603F97", 0.05),
          borderRadius: "4px",
        },
        "&::-webkit-scrollbar-thumb": {
          background: alpha("#603F97", 0.2),
          borderRadius: "4px",
          "&:hover": {
            background: alpha("#603F97", 0.3),
          },
        },
      }}
    >
      <Table stickyHeader>
        <TableHead>
          <TableRow>
            {[
              "CA Name",
              "Schedule Type",
              "Summary",
              "Validity Period",
              "Years",
              "Allowance Type",
              "Details",
              "Actions",
            ].map((header) => (
              <TableCell
                key={header}
                align='center'
                sx={{
                  fontWeight: 600,
                  backgroundColor: "primary.main",
                  color: "text.primary",
                  borderBottom: "2px solid",
                  borderColor: "primary.main",
                  padding: "16px",
                  fontSize: "0.875rem",
                  whiteSpace: "nowrap",
                  color: "white",
                }}
              >
                {header}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>

        <TableBody>
          {caSettings.length === 0 ? (
            <TableRow>
              <TableCell colSpan={8}>
                <Box
                  sx={{
                    py: 8,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 2,
                  }}
                >
                  <TimelineIcon sx={{ fontSize: 48, color: "text.disabled" }} />
                  <Typography variant='body1' color='text.secondary'>
                    No CA settings available
                  </Typography>
                  <Typography variant='body2' color='text.disabled'>
                    Click "Add CA" above to create your first CA setting
                  </Typography>
                </Box>
              </TableCell>
            </TableRow>
          ) : (
            caSettings.map((ca) => (
              <React.Fragment key={ca.key}>
                <TableRow
                  hover
                  sx={{
                    backgroundColor: expandedRows[ca.key]
                      ? alpha("#603F97", 0.03)
                      : "inherit",
                    transition: "all 0.2s ease-in-out",
                    "&:hover": {
                      backgroundColor: alpha("#603F97", 0.05),
                    },
                  }}
                >
                  <TableCell>
                    <Typography variant='subtitle2' fontWeight={600}>
                      {ca.key}
                    </Typography>
                  </TableCell>
                  <TableCell align='center'>
                    <Chip
                      label={ca.caType || "Not Set"}
                      size='small'
                      color={ca.caType ? "primary" : "default"}
                      variant={ca.caType ? "filled" : "outlined"}
                      sx={{
                        fontWeight: 500,
                        backgroundColor: ca.caType
                          ? alpha("#603F97", 0.1)
                          : "transparent",
                        color: ca.caType ? "primary.main" : "text.disabled",
                      }}
                    />
                  </TableCell>
                  <TableCell align='center'>
                    <Typography
                      variant='body2'
                      color={ca.caSummary ? "text.primary" : "text.disabled"}
                    >
                      {ca.caSummary || "Not Set"}
                    </Typography>
                  </TableCell>
                  <TableCell align='center'>
                    {ca.startPeriod ? (
                      <Stack
                        direction='row'
                        spacing={1}
                        alignItems='center'
                        justifyContent='center'
                      >
                        <CalendarTodayIcon
                          sx={{ fontSize: "1rem", color: "primary.main" }}
                        />
                        <Typography variant='body2'>
                          {ca.startPeriod} - {ca.endPeriod}
                        </Typography>
                      </Stack>
                    ) : (
                      <Typography variant='body2' color='text.disabled'>
                        Not Set
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align='center'>
                    <Chip
                      label={
                        ca.isFieldVariable.numberOfYears
                          ? "Variable"
                          : ca.numberOfYears || "Not Set"
                      }
                      size='small'
                      color={
                        ca.isFieldVariable.numberOfYears ? "primary" : "default"
                      }
                      variant={
                        ca.isFieldVariable.numberOfYears ? "filled" : "outlined"
                      }
                      sx={{
                        fontWeight: 500,
                        backgroundColor: ca.isFieldVariable.numberOfYears
                          ? alpha("#603F97", 0.1)
                          : "transparent",
                        color: ca.isFieldVariable.numberOfYears
                          ? "primary.main"
                          : "text.disabled",
                      }}
                    />
                  </TableCell>
                  <TableCell align='center'>
                    <Chip
                      label={ca.allowanceType.IA ? "AA + IA" : "AA Only"}
                      size='small'
                      color={ca.allowanceType.IA ? "primary" : "default"}
                      variant={ca.allowanceType.IA ? "filled" : "outlined"}
                      sx={{
                        fontWeight: 500,
                        backgroundColor: ca.allowanceType.IA
                          ? alpha("#603F97", 0.1)
                          : "transparent",
                        color: ca.allowanceType.IA
                          ? "primary.main"
                          : "text.disabled",
                      }}
                    />
                  </TableCell>
                  <TableCell align='center'>
                    <Tooltip
                      title={
                        expandedRows[ca.key] ? "Hide details" : "Show details"
                      }
                    >
                      <IconButton
                        onClick={() => toggleExpand(ca.key)}
                        size='small'
                        sx={{
                          color: expandedRows[ca.key]
                            ? "primary.main"
                            : "text.secondary",
                          transition: "transform 0.2s",
                          transform: expandedRows[ca.key]
                            ? "rotate(180deg)"
                            : "none",
                        }}
                      >
                        <ExpandMoreIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                  <TableCell align='center'>
                    <Tooltip
                      title={
                        ca.isEditable ? "More actions" : "Cannot edit this CA"
                      }
                    >
                      <span>
                        <IconButton
                          onClick={(event) => handleMenuOpen(event, ca)}
                          disabled={!ca.isEditable}
                          size='small'
                          sx={{
                            color: "text.secondary",
                            "&:hover": {
                              color: "primary.main",
                              backgroundColor: alpha("#603F97", 0.1),
                            },
                          }}
                        >
                          <MoreVertIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>

                {/* Expanded Details */}
                <TableRow>
                  <TableCell colSpan={8} sx={{ p: 0 }}>
                    <Collapse
                      in={expandedRows[ca.key]}
                      timeout='auto'
                      unmountOnExit
                    >
                      <Box
                        sx={{
                          py: 3,
                          px: 4,
                          backgroundColor: alpha("#603F97", 0.02),
                        }}
                      >
                        <Stack spacing={3}>
                          {/* Header */}
                          <Stack
                            direction='row'
                            spacing={1}
                            alignItems='center'
                          >
                            <InfoOutlinedIcon
                              color='primary'
                              fontSize='small'
                            />
                            <Typography
                              variant='subtitle2'
                              fontWeight={600}
                              color='primary.main'
                            >
                              Additional Details
                            </Typography>
                          </Stack>

                          {/* Main Content */}
                          <Grid container>
                            {/* Base Settings */}
                            <Grid item xs={12} md={6} sx={{ mr: 2 }}>
                              <Paper
                                variant='outlined'
                                sx={{
                                  p: 2,
                                  borderRadius: 2,
                                  borderColor: alpha("#603F97", 0.2),
                                }}
                              >
                                <Stack spacing={2}>
                                  <Typography
                                    variant='subtitle2'
                                    fontWeight={600}
                                  >
                                    Base Settings
                                  </Typography>
                                  <Stack spacing={2}>
                                    <Box>
                                      <Typography
                                        variant='body2'
                                        color='text.secondary'
                                        gutterBottom
                                      >
                                        Base Allowance Cap
                                      </Typography>
                                      <StatusChip
                                        enabled={ca.baseAllowanceCap}
                                      />
                                    </Box>
                                    {ca.baseAllowanceCap && (
                                      <Box>
                                        <Typography
                                          variant='body2'
                                          color='text.secondary'
                                          gutterBottom
                                        >
                                          Cap Amount
                                        </Typography>
                                        <Typography
                                          variant='subtitle2'
                                          fontWeight={500}
                                        >
                                          ${ca.cap?.toLocaleString() ?? "-"}
                                        </Typography>
                                      </Box>
                                    )}
                                  </Stack>
                                </Stack>
                              </Paper>
                            </Grid>

                            {/* Enhanced Settings */}
                            <Grid item xs={12} md={5.8}>
                              <Paper
                                variant='outlined'
                                sx={{
                                  p: 2,
                                  borderRadius: 2,
                                  borderColor: alpha("#603F97", 0.2),
                                }}
                              >
                                <Stack spacing={2}>
                                  <Typography
                                    variant='subtitle2'
                                    fontWeight={600}
                                  >
                                    Enhanced Settings
                                  </Typography>
                                  <Stack spacing={2}>
                                    <Box>
                                      <Typography
                                        variant='body2'
                                        color='text.secondary'
                                        gutterBottom
                                      >
                                        Enhanced Allowance
                                      </Typography>
                                      <StatusChip
                                        enabled={ca.enhancedAllowance}
                                      />
                                    </Box>
                                    {ca.enhancedAllowance && (
                                      <>
                                        <Box>
                                          <Typography
                                            variant='body2'
                                            color='text.secondary'
                                            gutterBottom
                                          >
                                            Enhanced Percentage
                                          </Typography>
                                          <Typography
                                            variant='subtitle2'
                                            fontWeight={500}
                                          >
                                            {ca.enhancedAllowancePercentage}%
                                          </Typography>
                                        </Box>
                                        <Box>
                                          <Typography
                                            variant='body2'
                                            color='text.secondary'
                                            gutterBottom
                                          >
                                            Further Cap Amount
                                          </Typography>
                                          <Typography
                                            variant='subtitle2'
                                            fontWeight={500}
                                          >
                                            $
                                            {ca.furtherAllowanceCap?.toLocaleString() ??
                                              "-"}
                                          </Typography>
                                        </Box>
                                      </>
                                    )}
                                  </Stack>
                                </Stack>
                              </Paper>
                            </Grid>
                          </Grid>

                          {/* AA Breakdown */}
                          {ca.AAbreakdown.length > 0 && (
                            <Paper
                              variant='outlined'
                              sx={{
                                p: 2,
                                borderRadius: 2,
                                borderColor: alpha("#603F97", 0.2),
                                bgcolor: "background.paper",
                              }}
                            >
                              <Stack spacing={2}>
                                <Typography
                                  variant='subtitle2'
                                  fontWeight={600}
                                >
                                  Annual Allowance Breakdown
                                </Typography>
                                <Box
                                  sx={{
                                    display: "grid",
                                    gridTemplateColumns:
                                      "repeat(auto-fill, minmax(120px, 1fr))",
                                    gap: 1,
                                  }}
                                >
                                  {ca.AAbreakdown.map((value, index) => (
                                    <Paper
                                      key={index}
                                      variant='outlined'
                                      sx={{
                                        p: 1.5,
                                        borderRadius: 1,
                                        borderColor: alpha("#603F97", 0.1),
                                        bgcolor: alpha("#603F97", 0.02),
                                        textAlign: "center",
                                      }}
                                    >
                                      <Typography
                                        variant='caption'
                                        color='text.secondary'
                                        display='block'
                                      >
                                        Year {index + 1}
                                      </Typography>
                                      <Typography
                                        variant='subtitle2'
                                        fontWeight={600}
                                        color='primary.main'
                                      >
                                        {value}%
                                      </Typography>
                                    </Paper>
                                  ))}
                                </Box>
                              </Stack>
                            </Paper>
                          )}
                        </Stack>
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            ))
          )}
        </TableBody>
      </Table>

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          elevation: 2,
          sx: {
            mt: 1,
            minWidth: 180,
            borderRadius: 2,
            backgroundColor: "background.paper",
          },
        }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        <MenuItem
          onClick={() => {
            handleEdit(selectedCa);
            handleMenuClose();
          }}
          sx={{
            py: 1,
            "&:hover": {
              backgroundColor: alpha("#603F97", 0.08),
            },
          }}
        >
          <ListItemIcon>
            <EditIcon fontSize='small' color='primary' />
          </ListItemIcon>
          <ListItemText
            primary='Edit'
            primaryTypographyProps={{
              variant: "body2",
              fontWeight: 500,
            }}
          />
        </MenuItem>
        <MenuItem
          onClick={handleDelete}
          sx={{
            py: 1,
            "&:hover": {
              backgroundColor: alpha("#D32F2F", 0.08),
            },
          }}
        >
          <ListItemIcon>
            <DeleteIcon fontSize='small' color='error' />
          </ListItemIcon>
          <ListItemText
            primary='Remove'
            primaryTypographyProps={{
              variant: "body2",
              fontWeight: 500,
              color: "error.main",
            }}
          />
        </MenuItem>
      </Menu>
    </TableContainer>
  );
};

export default CaSettingsTable;
